import { Record as record, List } from 'immutable';
import _ from 'lodash';
import moment from 'moment';

import { construct, fields, resolve, resolveAll } from '../../helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../helpers/Records';
import {
    SmartcardStatus,
    SmartcardProductStatusGroup,
    PassengerTypes
} from '../../constants/AppConstants';

import Railcard from '../Railcard';
import SmartcardProduct from './SmartcardProduct';
import KeyGo from './keygo/KeyGo';

const schema = {
    reference: [ fields.string ],
    cardType: [ fields.string ],
    cardStatus: [ fields.string ],
    expiryDate: [ fields.datetime ],
    isrn: [ fields.string ],
    cardDescription: [ fields.string ],
    availableForMoreProducts: [ fields.boolean ],
    passengerType: [ fields.string ],
    productAvailability: [ fields.number ],
    replacementInProgress: [ fields.boolean ],
    products: [ resolveAll.as(SmartcardProduct), new List() ],
    railcard: [ resolve.as(Railcard) ],
    autoDrOptedIn: [ fields.boolean ],
    keyGo: [ resolve.as(KeyGo) ],
    cardHolderDateOfBirth: [ fields.string ],
};


class Smartcard extends record(getDefaults(schema)) {
    static fromJSON(json) {
        return construct(Smartcard, json, getResolvers(schema));
    }

    getStatus () {
        return (this.get('cardStatus') || '').toUpperCase();
    }

    getCardNumber () {
        const { isrn } = this;
        const re = /(\d{6})(\d{4})(\d{4})(\d{4})/;
        const match = isrn && isrn.match(re);
        if (match) {
            return _.tail(match).join('-');
        }
        return null;
    }

    isActive () {
        const status = this.getStatus();
        return status === SmartcardStatus.ACTIVE
            || status === SmartcardStatus.HOTLIST_ERROR;
    }

    isPending () {
        return this.getStatus() === SmartcardStatus.PENDING;
    }

    isHotlisted () {
        const status = this.getStatus();
        return status === SmartcardStatus.HOTLISTED
            || status === SmartcardStatus.HOTLIST_REQUESTED
            || status === SmartcardStatus.HOTLIST_ACCEPTED
            || status === SmartcardStatus.HOTLIST_LIVE;
    }

    isReplacementPending () {
        return this.isPending() || this.getStatus() === SmartcardStatus.REQUESTED; // SEFT is `REQUESTED`
    }

    isAwaitingProduct () {
        return this.getStatus() === SmartcardStatus.AWAITING_PRODUCT;
    }

    canSearchForProducts () {
        return this.isActive() || this.isAwaitingProduct();
    }

    //  A smartcard can load a product if its ACTIVE
    //  or if its preregistered, and awaiting a product to be loaded before it is created
    canLoadProduct () {
        return this.isAwaitingProduct() || (this.isActive() && this.get('availableForMoreProducts'));
    }

    isActiveAndAvailableForMoreProducts() {
        return this.isActive() && this.get('availableForMoreProducts');
    }

    isFull () {
        return this.get('productAvailability') === 0;
    }

    getKeyGo () {
        return this.keyGo;
    }

    canRegisterKeyGo () {
        const keyGo = this.getKeyGo();
        return !keyGo || (keyGo && keyGo.isClosed());
    }

    hasKeyGo () {
        return this.getKeyGo() &&
            (this.getKeyGo().isActive() || this.getKeyGo().isPendingClosure());
    }

    hasActiveKeyGo () {
        return this.getKeyGo() && (this.getKeyGo().isActive() || this.getKeyGo().isPending());
    }

    getAllKeyGoProducts () {
        const keyGoProducts = this.products.filter((product) => product.isKeyGo());
        return keyGoProducts.sort((a, b) => {
                return a.parameters.IssueDate.isAfter(b.parameters.IssueDate) ? -1 : 1;
        });
    }

    getKeyGoProduct () {
        return this.getAllKeyGoProducts() && this.getAllKeyGoProducts().get(0);
    }

    getPendingProducts () {
        const statuses = SmartcardProductStatusGroup.PENDING;
        return this.products.filter((product) => statuses.includes(product.status));
    }

    getCurrentProducts () {
        const statuses = SmartcardProductStatusGroup.CURRENT;
        return this.products.filter((product) => statuses.includes(product.status));
    }

    isChild () {
        const passengerType = this.get('passengerType');
        return passengerType && passengerType.toLowerCase() === PassengerTypes.CHILD;
    }

    canActivate () {
        return this.isActive() && this.products.isEmpty();
    }

    hasProductsForOrder (orderId) {
        return this.products.some((product) => product.getOrderId() === Number(orderId));
    }

    getSecondsUntilAdult () {
        if (!this.cardHolderDateOfBirth) {
            return null;
        }
        const today = moment();
        const adulthoodDate = moment(this.cardHolderDateOfBirth).add(16, 'years');
        return adulthoodDate.diff(today, 'seconds');
    }

    willBeAdultInLessThanAMonth () {
        if (!this.cardHolderDateOfBirth) {
            return false;
        }
        const ageInMonths = moment().diff(this.cardHolderDateOfBirth, 'months');
        const fifteenYearsElevenMonths = 191;
        return ageInMonths === fifteenYearsElevenMonths;
    }
}


export default Smartcard;



// WEBPACK FOOTER //
// ./src/records/customer/Smartcard.js